import { useState, useEffect, useCallback } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { compose, isNil, not, path } from 'ramda'
import { hasPlanEnded } from 'commons/plan'
import { map, maybeToNullable } from 'sanctuary'
import { getToken } from 'app/services/userService/userStorage'
import { refreshTokenAction, loginWithSSOAction } from 'app/auth/store/actions'
import { viewToken, viewTokenAccess } from './store/reducers/user.reducer'
import { nullableToMaybe } from 'libs/maybe'
import { accessTokenToAuthorizationHeaders } from './authUtils'

export const useRefreshTokenOnLaunch = () => {
  const dispatch = useDispatch()

  const [waitAuthCheck, setWaitAuthCheck] = useState(true)
  const dispatchRefreshToken = useCallback(
    ({ refreshToken, maModeKey }) =>
      refreshTokenAction({ refreshToken, maModeKey })(dispatch),
    [dispatch],
  )

  const loginWithSSOCallback = useCallback(
    (token, maModeKey) => loginWithSSOAction(token, maModeKey, dispatch),
    [dispatch],
  )

  const params = new URLSearchParams(window.location.search)
  const ssoToken = params.get('token')
  const maModeKey = params.get('ma_mode_key')

  const isSSOPath = window.location.pathname === '/sso'

  const accessToken = useAccessToken()
  useEffect(() => {
    if (!waitAuthCheck) return

    if (accessToken) {
      setWaitAuthCheck(false)
      return
    }

    if (ssoToken && isSSOPath) {
      loginWithSSOCallback(ssoToken, maModeKey).then(() => {
        setWaitAuthCheck(false)
      })
      return
    }

    const tokenData = getToken()
    if (tokenData) {
      dispatchRefreshToken(tokenData).then(() => {
        setWaitAuthCheck(false)
      })
    } else {
      setWaitAuthCheck(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatchRefreshToken, accessToken, ssoToken, isSSOPath, waitAuthCheck])

  return waitAuthCheck
}

export const useIsInLimboPeriod = () => {
  const subscription = useSelector(path(['place', 'activeSubscription']))
  return hasPlanEnded({ subscription })
}

export const useIsPlacePendingActivateHasAccess = () => {
  return useSelector(path(['place', 'info', 'pendingApproval']))
}

export const useIsAuthorized = () => compose(not, isNil)(useSelector(viewToken))
export const useToken = () => useSelector(viewToken)
export const useAccessToken = () => useSelector(viewTokenAccess)

export const usePrivateHeaders = compose(
  maybeToNullable,
  map(accessTokenToAuthorizationHeaders),
  nullableToMaybe,
  useToken,
)
